import {defineStore} from "pinia";
import {useAuthStore} from "@/stores/authStores";
import {useICPStore} from "@/stores/icpStore";
import {useGroupCallsStore} from "@/stores/groupCallsStore";
import {useMediaStore} from "@/stores/mediaStore";
import api from '@/services/api'
import {useContactsStore} from "@/stores/contactsStore";

export const useCallsStore = defineStore(
    'calls',
    {
        state: () => ({
            calls: [],
            selectedCall: [],
            callHistory: [],
            intercepting: {intercept: false, to: ""}
        }),

        getters: {

            incomingCalls: state => {
                return state.calls.filter(call => call.status === 'Ringing' && call.direction === "Incoming")
                    .sort((a, b) => {
                        if (a.type === 'Emergency' && b.type !== 'Emergency') return -1;
                        if (a.type !== 'Emergency' && b.type === 'Emergency') return 1;
                        return a.time - b.time;
                    });
            },

            activeCalls: state => {
                return state.calls.filter(call => (call.status === 'Ringing' && call.direction === "Outgoing") || (call.status === "Transmitting") || (call.status === "Calling") || (call.status === "Holding"))
                    .sort((a, b) => {
                        if (a.type === 'Emergency' && b.type !== 'Emergency') return -1;
                        if (a.type !== 'Emergency' && b.type === 'Emergency') return 1;
                        return a.time - b.time;
                    });
            },

            history: state => {
                // Sort the call history by endTime in descending order
                return state.callHistory.sort((a, b) => {
                    return b.endTime - a.endTime;
                });
            }

        },

        actions: {
            setCallVolume(newVolume) {
                this.selectedCall[0].volume = newVolume;
                useICPStore().ICP.dispatch.device.setVolume({
                    "cid": this.selectedCall[0].cid,
                    "volume": newVolume.toString(),
                    "callback": () => {
                    }
                })
            },

            /**

            setSelectedCall(item) {
                if (item) {
                    useGroupCallsStore().setSelectedCall([]);
                    this.selectedCall = [item]
                } else {
                    this.selectedCall = [];
                }
                useMediaStore().alignShowValue();// check if selected call is in active calls
                if (this.activeCalls.includes(item)) {
                    this.activeCalls.forEach(call => {
                        if (!item || call.cid !== item.cid) {
                            useICPStore().ICP.dispatch.voice.hold({
                                "cid": call.cid,
                                "callback": () => {
                                }
                            })
                        } else {
                            useICPStore().ICP.dispatch.voice.unhold({
                                "cid": call.cid,
                                "callback": () => {
                                }
                            })
                        }
                    });
                }
            },

                */

            setSelectedCall(item) {
                const holdable = (c) => c && (c.status === "Transmitting" || c.status === "Holding");
                const emergency = this.calls.find(c => c.type === "Emergency" && holdable(c));

                // Only override selection if user is trying to focus an established call while emergency is active
                if (emergency && item && item.type !== "Emergency" && holdable(item)) {
                    item = emergency;
                }

                // --- keep the rest as you had it ---
                if (item) {
                    useGroupCallsStore().setSelectedCall([]);
                    this.selectedCall = [item]
                } else {
                    this.selectedCall = [];
                }

                useMediaStore().alignShowValue();

                // IMPORTANT: don't rely on object identity
                if (item && this.activeCalls.some(c => c.cid === item.cid)) {
                    this.activeCalls.forEach(call => {
                        if (!item || call.cid !== item.cid) {
                            useICPStore().ICP.dispatch.voice.hold({ cid: call.cid, callback: () => {} })
                        } else {
                            useICPStore().ICP.dispatch.voice.unhold({ cid: call.cid, callback: () => {} })
                        }
                    });
                }
            },

            enforceEmergencyPriority() {
                const emergency = this.calls.find(c =>
                    c.type === "Emergency" && (c.status === "Transmitting" || c.status === "Holding")
                );
                if (!emergency) return;

                const holdable = (c) => c.status === "Transmitting" || c.status === "Holding";

                // Keep emergency unheld
                if (holdable(emergency)) {
                    useICPStore().ICP.dispatch.voice.unhold({ cid: emergency.cid, callback: () => {} });
                }

                // Hold all other established calls
                this.calls.forEach(c => {
                    if (c.cid === emergency.cid) return;
                    if (!holdable(c)) return;
                    useICPStore().ICP.dispatch.voice.hold({ cid: c.cid, callback: () => {} });
                });

                // Keep emergency selected in UI
                this.selectedCall = [emergency];
            },

            receiveCall(data) {
                let newCall = {};
                if (this.intercepting.intercept && this.intercepting.to === data.value.caller) {
                    newCall = {
                        cid: data.value.cid,
                        caller: data.value.caller,
                        callee: data.value.callee,
                        trainNum: "Unknown (" + data.value.caller + ")",
                        runAndDriveNum: "-",
                        zoneId: "Unknown",
                        direction: "Outgoing",
                        status: "Ringing",
                        volume: useMediaStore().defaultValue,
                        time: Date.now(),
                        type: "Emergency"
                    }
                } else {
                    newCall = {
                        cid: data.value.cid,
                        caller: data.value.caller,
                        callee: data.value.callee,
                        trainNum: "Unknown (" + data.value.caller + ")",
                        runAndDriveNum: "-",
                        zoneId: "Unknown",
                        direction: "Incoming",
                        status: "Ringing",
                        volume: useMediaStore().defaultValue,
                        time: Date.now(),
                        type: "Normal"
                    }
                }
                this.calls.push(newCall);
                this.fetchIncomingCall(data.value.caller, data.value.cid);
            },

            startNewCall(data) {
                const newCall = {
                    cid: data.value.cid,
                    caller: useAuthStore().loggedUser,
                    callee: data.value.callee,
                    trainNum: "Unknown (" + data.value.callee + ")",
                    runAndDriveNum: "-",
                    zoneId: "Unknown",
                    direction: "Outgoing",
                    status: "Calling",
                    volume: useMediaStore().defaultValue,
                    time: Date.now(),
                    type: "Normal"
                };

                this.calls.push(newCall);
                this.setSelectedCall(newCall);
                this.fetchIncomingCall(data.value.callee, data.value.cid);
            },

            answerCall(data) {
                var index = 0;
                index = this.calls.findIndex(c => c.cid === data.value.cid);
                if (index !== -1) {
                    this.calls[index].status = 'Transmitting';
                    // If emergency exists, don't steal focus away from it
                    this.enforceEmergencyPriority();

                    if (!this.calls.some(c => c.type === "Emergency" && (c.status === "Transmitting" || c.status === "Holding"))) {
                        this.setSelectedCall(this.calls[index]);
                    }

                    this.calls[index].volume = useMediaStore().defaultValue;
                    this.setSelectedCall(this.calls[index]);
                    useICPStore().ICP.dispatch.device.setVolume({
                        "cid": data.value.cid,  // default volume
                        "volume": useMediaStore().defaultValue.toString(),
                        "callback": () => {
                        }
                    })
                } else {
                    // Supposing this is an emergency call that was answered directly
                    const newCall = {
                        cid: data.value.cid,
                        caller: data.value.caller,
                        callee: data.value.callee,
                        trainNum: "Unknown (" + data.value.caller + ")",
                        runAndDriveNum: "-",
                        zoneId: "Unknown",
                        direction: "Incoming",
                        status: "Transmitting",
                        volume: useMediaStore().defaultValue,
                        time: Date.now(),
                        type: "Emergency"
                    };

                    this.calls.push(newCall);
                    this.setSelectedCall(newCall);
                    useMediaStore().selectedTab = "RADIO";
                    this.fetchIncomingCall(data.value.caller, data.value.cid);

                }
            },

            rejectCall(selected) {
                const index = this.calls.findIndex(c => c === selected);
                if (index !== -1) {
                    this.calls[index].startTime = this.calls[index].time;
                    this.calls[index].endTime = Date.now();
                    delete this.calls[index].time;
                    this.calls[index].status = 'Rejected';
                    this.pushToHistory(this.calls[index])
                    this.calls.splice(index, 1);
                }
            },

            /**
             updateCallStatus(data, status) {
             const index = this.calls.findIndex(c => c.cid === data.value.cid);
             // check if there is any emergency call in action
             // if there is an emergency call, unhold that one, and put all the other in hold. Else update the status of the call normally
             if (index !== -1) {
             this.calls[index].status = status;
             }
             },
             */

            updateCallStatus(data, status) {
                const index = this.calls.findIndex(c => c.cid === data.value.cid);
                if (index === -1) return;

                this.calls[index].status = status;

                // enforce policy after any status update
                this.enforceEmergencyPriority();
            },


            releaseCall(data) {
                const index = this.calls.findIndex(c => c.cid === data.value.cid);
                if (index !== -1) {
                    this.calls[index].startTime = this.calls[index].time;
                    this.calls[index].endTime = Date.now();
                    const currStatus = this.calls[index].status
                    if (currStatus === 'Transmitting') {
                        this.calls[index].status = 'Ended';
                    } else if (currStatus === 'Ringing' || currStatus === 'Calling') {
                        this.calls[index].status = 'Missed';
                    }
                    this.pushToHistory(this.calls[index]);
                    this.calls.splice(index, 1);
                }
            },

            pushToHistory(call) {
                // if active calls is not empty, set selected call to the first active call
                if (this.activeCalls.length > 0) {
                    this.setSelectedCall(this.activeCalls[0]);
                } else {
                    this.selectedCall = [];
                }
                useMediaStore().alignShowValue();
                api.post(
                    '/individualCall/addToHistory',
                    call,
                    {headers: {'Content-Type': 'application/json'}}
                )
                    .then(() => {
                        this.fetchHistory();
                    })
                    .catch(error => {
                        console.error("There was an error adding the call to history: ", error);
                    });
            },

            fetchHistory() {
                api.get('/individualCall/getHistory', {
                    params: {
                        user: useAuthStore().loggedUser
                    }
                })
                    .then(response => {
                        this.callHistory = response.data;
                    })
                    .catch(error => {
                        console.error("There was an error fetching the call history: ", error);
                    });
            },

            fetchIncomingCall(caller, cid) {
                // first check if the caller a handheld
                const contactsStore = useContactsStore();
                const hhIndex = contactsStore.handheld.findIndex(hh => hh.isdn === caller);
                const callIndex = this.calls.findIndex(c => c.cid === cid);
                if (hhIndex !== -1) {
                    this.calls[callIndex].trainNum = contactsStore.handheld[hhIndex].radioId + " (" + caller + ")";
                    this.calls[callIndex].zoneId = "-";
                    this.calls[callIndex].runAndDriveNum = "-";

                } else {
                    api.get('/train/getTrainDetailByIsdn', {
                        params: {
                            user: caller
                        }
                    })
                        .then(response => {
                            if (response.data !== "") {
                                this.calls[callIndex].trainNum = response.data.trainId
                                this.calls[callIndex].zoneId = response.data.location
                                this.calls[callIndex].runAndDriveNum = response.data.runNumber + " - " + response.data.driverNumber
                            }
                        })
                        .catch(error => {
                            console.error("There was an error fetching data of the caller: ", error);
                        });
                }
            }
        }
    }
)